import { confirmAlert } from "react-confirm-alert";
import ImageGeneric from "@/assets/images/lg.png";
import { toast } from "react-toastify";
import notify from "@/services/notify.tsx";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useLayout } from "../../../../contexts/UseLayout";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import { renderMessageContent } from "@/components/utils/Utils";
import { File, Paperclip, X, Download } from "lucide-react";
import { useTranslation } from "react-i18next";

const MAX_COMMENT_INPUT_HEIGHT = 128;

function resizeCommentInput(element: HTMLTextAreaElement) {
  element.style.height = "auto";
  const nextHeight = Math.min(element.scrollHeight, MAX_COMMENT_INPUT_HEIGHT);
  element.style.height = `${nextHeight}px`;
  element.style.overflowY =
    element.scrollHeight > MAX_COMMENT_INPUT_HEIGHT ? "auto" : "hidden";
}

export function ComentariosTarefa({ parentId, type }: any) {
  const { t } = useTranslation();
  const context = useLayout();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const { register, handleSubmit, reset } = useForm();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null);
  const contentField = register("content", {
    onChange: (event) =>
      resizeCommentInput(event.target as HTMLTextAreaElement),
  });

  const {
    isPending,
    data: comentarios,
    refetch,
  } = useQuery({
    queryKey: [`comentarios-${type}`, parentId],
    queryFn: async () => await ServiceResumoDesk.getComentarios(parentId, type),
    retry: 3,
  });

  const dadosReversos = comentarios
    ? [...comentarios.data].sort(
        (a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
    : [];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (payload: any) => {
    if (!payload.content.trim() && selectedFiles.length === 0) {
      notify.warning(t("tasks.comments.errors.empty"));
      return;
    }

    const toastId = notify.loading("Enviando comentário...");
    try {
      setIsSubmitting(true);

      const formData = new FormData();

      if (type === "tarefa") {
        formData.append("tarefa_id", parentId);
      } else if (type === "nota") {
        formData.append("nota_id", parentId);
      }

      if (payload.content) {
        formData.append("content", payload.content);
      }

      selectedFiles.forEach((file) => {
        formData.append("files", file);
      });

      const res = await ServiceResumoDesk.addComentarios(formData);

      if (res.status === 201) {
        notify.update(toastId, "success", "Comentário enviado!");
        reset();
        requestAnimationFrame(() => {
          if (commentInputRef.current) resizeCommentInput(commentInputRef.current);
        });
        setSelectedFiles([]);
        refetch();
        queryClient.invalidateQueries({ queryKey: ["dataNotificacoes"] });
        queryClient.invalidateQueries({ queryKey: ["comentarios-tarefas"] });
      }
    } catch (error: any) {
      notify.update(
        toastId,
        "error",
        error?.response?.data?.msg ||
          error?.response?.data?.error ||
          "Erro ao enviar comentário",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  async function deleteComentario(id: any) {
    confirmAlert({
      title: t("common:confirmation"),
      message: t("tasks.comments.delete_confirm"),
      buttons: [
        {
          label: t("common:yes"),
          onClick: async () => {
            try {
              const res = await ServiceResumoDesk.delComentarios(id);
              if (res.status === 200) {
                toast.success("Deletado com sucesso!");
                refetch();
              }
            } catch (error) {
              console.error("Erro:", error);
            }
          },
        },
        {
          label: t("common:cancel"),
          onClick: () => {},
        },
      ],
    });
  }

  return (
    <div>
      <div className="flex gap-2 text-sm">
        <i className="bi bi-chat-left-text-fill text-sm text-[#797979]"></i>
        <h2>{t("tasks.comments.title")}</h2>
      </div>
      <form
        className="flex flex-col gap-2 my-2"
        onSubmit={handleSubmit(onSubmit)}
      >
        {/* Preview dos arquivos selecionados */}
        {selectedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border text-sm"
              >
                <File className="w-4 h-4 text-gray-500" />
                <span
                  className="font-medium truncate max-w-32"
                  title={file.name}
                >
                  {file.name}
                </span>
                <span className="text-xs text-gray-500">
                  {(file.size / 1024).toFixed(1)} KB
                </span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-700 ml-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-x-2">
          <img
            src={context?.userLogged?.data?.filePath ?? ImageGeneric}
            alt={context?.userLogged?.data?.name}
            className="w-6 h-6 rounded-full"
          />
          <div className="flex w-full items-end gap-x-2">
            <textarea
              {...contentField}
              ref={(element) => {
                contentField.ref(element);
                commentInputRef.current = element;
              }}
              rows={1}
              className="min-h-10 max-h-32 w-full resize-none overflow-y-hidden rounded-lg bg-zinc-100 px-2 py-2 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-azul-400"
              placeholder={t("tasks.comments.placeholder")}
            />

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              className="hidden"
              accept="*/*"
            />

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="h-10 w-10 flex-shrink-0 bg-gray-200 hover:bg-gray-300 flex items-center justify-center rounded-lg transition-colors"
              title={t("tasks.comments.attach")}
            >
              <Paperclip className="w-4 h-4 text-gray-600" />
            </button>

            <button
              disabled={isSubmitting}
              type="submit"
              className="h-10 w-10 flex-shrink-0 bg-azul-400 flex items-center justify-center rounded-lg disabled:opacity-50"
            >
              <i className="bi bi-send-fill text-xs text-white"></i>
            </button>
          </div>
        </div>
      </form>
      <div>
        {isPending && <p className="text-center">{t("common:loading")}</p>}
        {comentarios?.data.length === 0 && (
          <p className="text-center">{t("tasks.comments.none")}</p>
        )}
        {!isPending &&
          !!comentarios?.data &&
          dadosReversos?.map((e: any) => (
            <div
              className="flex items-start gap-2 my-2 bg-zinc-100 p-2 rounded-xl"
              key={e.id}
            >
              <img
                src={e.user.url_img ?? ImageGeneric}
                alt={e.user.name}
                className="w-6 h-6 rounded-full object-cover"
                title={`${e.user.name} ${e.user.last_name}`}
              />
              <div className="w-full">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {e.content && (
                      <p className="mb-2">{renderMessageContent(e.content)}</p>
                    )}

                    {/* Exibir anexos se existirem */}
                    {e?.files &&
                      e?.files !== "[]" &&
                      Array.isArray(e?.files) &&
                      e?.files.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {e?.files?.map((file: any, fileIndex: number) => (
                            <div
                              key={fileIndex}
                              className="flex items-center gap-2 p-2 rounded-lg bg-white border"
                            >
                              <File className="w-4 h-4 text-gray-500" />
                              <div className="flex-1 min-w-0">
                                <button
                                  type="button"
                                  title={file?.name || file?.path || file}
                                  onClick={() =>
                                    window.open(file?.path || file, "_blank")
                                  }
                                  className="text-sm font-medium truncate max-w-32 text-azul-400 hover:underline"
                                >
                                  {file?.name ||
                                    file?.path?.split("/").pop() ||
                                    file}
                                </button>
                                {file?.size && (
                                  <p className="text-xs text-gray-500">
                                    {(parseInt(file.size) / 1024).toFixed(1)} KB
                                  </p>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  window.open(file?.path || file, "_blank")
                                }
                                className="p-1 rounded hover:bg-gray-100"
                                title="Baixar arquivo"
                              >
                                <Download className="w-3 h-3 text-gray-500" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                  <button onClick={() => deleteComentario(e.id)}>
                    <i className="bi bi-trash-fill text-red-500 text-sm"></i>
                  </button>
                </div>
                <p className="text-[10px] text-end">
                  {moment(e.createdAt).format("DD/MM/YYYY - HH:mm")}
                </p>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
